import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Card, CardContent, Input } from '@agms/ui'
import { userAdminApi, type UserRole } from '../lib/api'
import { usePermissions } from '../hooks/useAuth'
import { apiErrorMessage } from '../hooks/useErrorHandler'
import { CardTitle, PageHeader, SurfaceCard } from '../components/ui'

const ROLES: UserRole[] = ['sales_rep', 'operations', 'management', 'support', 'finance', 'super_admin']

const ROLE_LABELS: Record<UserRole, string> = {
  sales_rep: 'Sales Rep',
  operations: 'Operations',
  management: 'Management',
  support: 'Support',
  finance: 'Finance',
  super_admin: 'Super Admin',
}

function roleClass(role: UserRole): string {
  const classes: Record<UserRole, string> = {
    sales_rep: 'bg-blue-50 text-blue-700',
    operations: 'bg-emerald-50 text-emerald-700',
    management: 'bg-purple-50 text-purple-700',
    support: 'bg-slate-100 text-slate-700',
    finance: 'bg-amber-50 text-amber-700',
    super_admin: 'bg-rose-50 text-rose-700',
  }
  return classes[role]
}

function formatLastLoginDate(value: string): string {
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatLastLoginTime(value: string): string {
  return new Date(value).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

const USER_ACTIONS_MENU_WIDTH = 224
const USER_ACTIONS_MENU_HEIGHT = 124
const STICKY_HEADER_HEIGHT = 58

function focusableOutsideMenu(menu: HTMLElement | null): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )).filter((element) => !menu?.contains(element))
}

function UserActionsMenu({
  userId,
  userName,
  onResetTwoFactor,
  onSendPasswordReset,
  onResendInvite,
}: {
  userId: string
  userName: string
  onResetTwoFactor: (id: string) => void
  onSendPasswordReset: (id: string) => void
  onResendInvite: (id: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([])
  const initialFocus = useRef(0)
  const menuId = `user-actions-${userId}`
  const label = `Actions for ${userName}`

  const placeMenu = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect()
    if (!rect) return
    let top = rect.bottom + 4
    const fitsBelow = top + USER_ACTIONS_MENU_HEIGHT <= window.innerHeight
    const fitsAbove = rect.top - USER_ACTIONS_MENU_HEIGHT - 4 >= STICKY_HEADER_HEIGHT
    if (!fitsBelow && fitsAbove) {
      top = rect.top - USER_ACTIONS_MENU_HEIGHT - 4
    }
    setPosition({
      top: Math.max(STICKY_HEADER_HEIGHT, top),
      left: Math.max(8, rect.right - USER_ACTIONS_MENU_WIDTH),
    })
  }, [])

  const items = useCallback(() => (
    itemRefs.current.filter((item): item is HTMLButtonElement => item !== null)
  ), [])

  const openMenu = (focusIndex: number) => {
    initialFocus.current = focusIndex
    placeMenu()
    setOpen(true)
  }

  const closeAndFocusTrigger = () => {
    setOpen(false)
    buttonRef.current?.focus()
  }

  const choose = (action: (id: string) => void) => {
    setOpen(false)
    action(userId)
    buttonRef.current?.focus()
  }

  useEffect(() => {
    if (!open) return
    const buttons = items()
    const index = initialFocus.current < 0 ? buttons.length - 1 : initialFocus.current
    buttons[index]?.focus()
  }, [items, open])

  useEffect(() => {
    if (!open) return
    const menu = menuRef.current

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (menu?.contains(target) || buttonRef.current?.contains(target)) return
      setOpen(false)
    }
    const onFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget as Node | null
      if (next && (menu?.contains(next) || buttonRef.current?.contains(next))) return
      // Safari and Firefox on macOS blur a pressed button without moving
      // focus anywhere, so relatedTarget is null and this event arrives
      // before click. Leave the menu up so the click can run. A press
      // outside still closes through the mousedown listener.
      if (next == null && menu?.contains(event.target as Node)) return
      setOpen(false)
    }
    const closeOnViewportChange = () => {
      setOpen(false)
      buttonRef.current?.focus()
    }

    document.addEventListener('mousedown', onPointerDown)
    menu?.addEventListener('focusout', onFocusOut)
    window.addEventListener('resize', closeOnViewportChange)
    window.addEventListener('scroll', closeOnViewportChange, true)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      menu?.removeEventListener('focusout', onFocusOut)
      window.removeEventListener('resize', closeOnViewportChange)
      window.removeEventListener('scroll', closeOnViewportChange, true)
    }
  }, [open])

  const onMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const buttons = items()
    const current = buttons.indexOf(document.activeElement as HTMLButtonElement)
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      buttons[(current + 1) % buttons.length]?.focus()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      buttons[(current - 1 + buttons.length) % buttons.length]?.focus()
    } else if (event.key === 'Home') {
      event.preventDefault()
      buttons[0]?.focus()
    } else if (event.key === 'End') {
      event.preventDefault()
      buttons[buttons.length - 1]?.focus()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      closeAndFocusTrigger()
    } else if (event.key === 'Tab') {
      event.preventDefault()
      const trigger = buttonRef.current
      const outside = focusableOutsideMenu(menuRef.current)
      const index = trigger ? outside.indexOf(trigger) : -1
      const next = outside[index + (event.shiftKey ? -1 : 1)] ?? trigger
      setOpen(false)
      next?.focus()
    }
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={label}
        onMouseDown={(event) => {
          if (open) event.preventDefault()
        }}
        onClick={() => {
          if (open) {
            setOpen(false)
            return
          }
          openMenu(0)
        }}
        onKeyDown={(event) => {
          if (open) return
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            openMenu(0)
          } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            openMenu(-1)
          }
        }}
        className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-sm font-medium text-slate-700 hover:bg-slate-50"
      >
        Actions
      </button>
      {open && position ? createPortal(
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={label}
          onMouseDown={(event) => event.preventDefault()}
          onKeyDown={onMenuKeyDown}
          style={{ position: 'fixed', top: position.top, left: position.left, width: USER_ACTIONS_MENU_WIDTH }}
          className="z-30 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
        >
          <button
            ref={(node) => { itemRefs.current[0] = node }}
            type="button"
            role="menuitem"
            tabIndex={-1}
            onClick={() => choose(onResetTwoFactor)}
            className="block w-full px-3 py-2 text-left text-sm text-amber-700 hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
          >
            Reset 2FA
          </button>
          <button
            ref={(node) => { itemRefs.current[1] = node }}
            type="button"
            role="menuitem"
            tabIndex={-1}
            onClick={() => choose(onSendPasswordReset)}
            className="block w-full px-3 py-2 text-left text-sm text-blue-700 hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
          >
            Send password reset
          </button>
          <button
            ref={(node) => { itemRefs.current[2] = node }}
            type="button"
            role="menuitem"
            tabIndex={-1}
            onClick={() => choose(onResendInvite)}
            className="block w-full px-3 py-2 text-left text-sm text-slate-600 hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
          >
            Resend invite
          </button>
        </div>,
        document.body,
      ) : null}
    </div>
  )
}

function AdminOnly({ children }: { children: React.ReactNode }) {
  const { canManageUsers } = usePermissions()

  if (!canManageUsers()) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <h1 className="text-lg font-semibold text-gray-900">User management is restricted</h1>
          <p className="mt-2 text-sm text-gray-500">Only super admins can manage CRM users.</p>
        </CardContent>
      </Card>
    )
  }

  return <>{children}</>
}

export function SettingsUsersPage() {
  const queryClient = useQueryClient()
  const { canManageUsers } = usePermissions()
  const [search, setSearch] = useState('')
  const [role, setRole] = useState<UserRole | ''>('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-users', search, role],
    queryFn: () => userAdminApi.list({ search: search || undefined, role: role || undefined }),
    enabled: canManageUsers(),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { is_active: boolean } }) => userAdminApi.update(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  })

  const resetMutation = useMutation({
    mutationFn: userAdminApi.resetTwoFactor,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  })

  const passwordResetMutation = useMutation({
    mutationFn: userAdminApi.sendPasswordReset,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  })

  const resendMutation = useMutation({
    mutationFn: userAdminApi.resendInvite,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  })

  const users = data?.data ?? []

  return (
    <AdminOnly>
      <div className="space-y-6">
        <PageHeader
          title="Users"
          subtitle="Create users, assign roles, disable access, and reset 2FA."
          action={(
            <Link to="/settings/users/new">
            <Button>New User</Button>
          </Link>
          )}
        />

        <SurfaceCard>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <Input
                type="search"
                aria-label="Search users"
                placeholder="Search by name or email..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="min-w-72 flex-1"
              />
              <select
                aria-label="User role filter"
                value={role}
                onChange={(event) => setRole(event.target.value as UserRole | '')}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
              >
                <option value="">All roles</option>
                {ROLES.map((item) => (
                  <option key={item} value={item}>{ROLE_LABELS[item]}</option>
                ))}
              </select>
            </div>

            {error && (
              <p className="rounded-md border border-red-100 bg-red-50 p-3 text-sm text-red-700">
                Failed to load users.
              </p>
            )}

            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">User</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Role</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">2FA</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Last Login</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {isLoading ? (
                    <tr>
                      <td className="px-4 py-8 text-center text-gray-500" colSpan={6}>Loading users...</td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td className="px-4 py-8 text-center text-gray-500" colSpan={6}>No users match the current filters.</td>
                    </tr>
                  ) : users.map((user) => (
                    <tr key={user.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{user.name}</div>
                        <div className="text-xs text-gray-500">{user.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${roleClass(user.role)}`}>
                          {ROLE_LABELS[user.role]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => updateMutation.mutate({ id: user.id, payload: { is_active: !user.is_active } })}
                          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${user.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}
                        >
                          {user.is_active ? 'Active' : 'Disabled'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {user.two_factor_confirmed_at ? 'Enrolled' : 'Required'}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {user.last_login_at ? (
                          <time dateTime={user.last_login_at} title={formatLastLoginTime(user.last_login_at)}>
                            {formatLastLoginDate(user.last_login_at)}
                          </time>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-3">
                          <Link to={`/settings/users/${user.id}`} className="text-sm text-blue-600 hover:text-blue-800">
                            Edit
                          </Link>
                          <UserActionsMenu
                            userId={user.id}
                            userName={user.name}
                            onResetTwoFactor={(id) => resetMutation.mutate(id)}
                            onSendPasswordReset={(id) => passwordResetMutation.mutate(id)}
                            onResendInvite={(id) => resendMutation.mutate(id)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </SurfaceCard>
      </div>
    </AdminOnly>
  )
}

export function SettingsUserFormPage({ mode }: { mode: 'create' | 'edit' }) {
  const { canManageUsers } = usePermissions()
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const { data: existing, isLoading } = useQuery({
    queryKey: ['admin-user', id],
    queryFn: () => userAdminApi.get(id as string),
    enabled: canManageUsers() && mode === 'edit' && !!id,
  })

  const initialRole = useMemo(() => existing?.role ?? 'sales_rep', [existing?.role])

  const mutation = useMutation({
    mutationFn: (payload: { name: string; email?: string; role: UserRole; is_active?: boolean }) => {
      if (mode === 'create') {
        return userAdminApi.create({
          name: payload.name,
          email: payload.email as string,
          role: payload.role,
        })
      }
      return userAdminApi.update(id as string, {
        name: payload.name,
        role: payload.role,
        is_active: payload.is_active,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      navigate('/settings/users')
    },
    onError: (err) => {
      setError(apiErrorMessage(err, 'Failed to save user.'))
    },
  })

  if (!canManageUsers()) {
    return <Navigate to="/settings" replace />
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)

    const form = new FormData(event.currentTarget)
    mutation.mutate({
      name: String(form.get('name') ?? ''),
      email: String(form.get('email') ?? ''),
      role: String(form.get('role') ?? 'sales_rep') as UserRole,
      is_active: form.get('is_active') === 'on',
    })
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/settings/users" className="text-sm text-blue-600 hover:text-blue-800">Back to users</Link>
        <PageHeader className="mt-2" title={mode === 'create' ? 'New User' : 'Edit User'} />
      </div>

      <SurfaceCard>
        <CardTitle>{mode === 'create' ? 'Create User' : 'User Details'}</CardTitle>
        <div className="mt-4">
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading user...</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input name="name" label="Name" required defaultValue={existing?.name ?? ''} />
              <Input
                name="email"
                label="Email"
                type="email"
                required
                disabled={mode === 'edit'}
                defaultValue={existing?.email ?? ''}
              />
              <div>
                <label htmlFor="role" className="mb-1 block text-sm font-medium text-gray-700">Role</label>
                <select
                  id="role"
                  name="role"
                  defaultValue={initialRole}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                >
                  {ROLES.map((item) => (
                    <option key={item} value={item}>{ROLE_LABELS[item]}</option>
                  ))}
                </select>
              </div>
              {mode === 'edit' && (
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" name="is_active" defaultChecked={existing?.is_active ?? true} />
                  Active
                </label>
              )}
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex justify-end gap-2">
                <Link to="/settings/users">
                  <Button type="button" variant="secondary">Cancel</Button>
                </Link>
                <Button type="submit" isLoading={mutation.isPending}>
                  {mode === 'create' ? 'Create User' : 'Save Changes'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </SurfaceCard>
    </div>
  )
}
